import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, withClientTimeout } from "../../lib/api";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Modal } from "../ui/Modal";

export function AgentBeaconKeyReplacement({ connectionId }: { connectionId: string }) {
  const id = useId(); const qc = useQueryClient();
  const [open, setOpen] = useState(false); const [key, setKey] = useState("");
  const [pending, setPending] = useState(false); const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const close = () => { if (!pending) { setOpen(false); setKey(""); setError(null); } };
  return <div>
    <Button variant="secondary" onClick={() => { setNotice(null); setError(null); setOpen(true); }}>Replace key</Button>
    {notice && <p role="status" className="mt-2 text-sm text-slate-600">{notice}</p>}
    <Modal open={open} onClose={close} title="Replace AgentBeacon key">
      <form className="space-y-4" onSubmit={async event => {
        event.preventDefault(); if (pending) return;
        setPending(true); setError(null);
        try {
          // Do not put credentials into React Query mutation variables/cache.
          const result = await withClientTimeout(signal => api.post<{ data: { rotated: boolean; cleanup_pending: boolean } }>(
            `/api/connections/agentbeacon/${encodeURIComponent(connectionId)}/key`, { api_key: key }, undefined, signal), 45_000);
          if (result.data.rotated !== true) throw Error("Unconfirmed replacement");
          setOpen(false);
          setNotice(result.data.cleanup_pending ? "Key replaced. Removal of the previous saved key is pending." : "Key replaced. Saved queries and schedules are unchanged.");
          void qc.invalidateQueries({ queryKey: ["account-connections"] }).catch(() => {});
        } catch (cause) {
          setError(cause instanceof ApiError && cause.status === 422
            ? "The replacement key could not access the same source. Check its permissions and try again."
            : cause instanceof ApiError && cause.status === 429
              ? "Too many key attempts. Wait before trying again."
              : "Replacement could not be confirmed. Reload this connection before retrying; the change may have succeeded.");
        } finally { setKey(""); setPending(false); }
      }}>
        <p className="text-sm text-slate-600">Use a read key for the same AgentBeacon tenant, service and environment. This keeps your connection, brand assignments, queries and schedules.</p>
        <Input id={`${id}-replacement`} label="Replacement read key" type="password" autoComplete="off" required maxLength={4096} disabled={pending} value={key} onChange={event => setKey(event.target.value)} />
        {error && <p role="alert" className="text-sm text-error">{error}</p>}
        <div className="flex gap-2"><Button type="button" variant="secondary" disabled={pending} onClick={close}>Cancel</Button><Button type="submit" loading={pending} disabled={!key}>Save replacement key</Button></div>
      </form>
    </Modal>
  </div>;
}
